import * as Yup from 'yup';

import { BIOLOGICAL_SEX_VALUES, EXTENDED_RELATIONSHIP_VALUES, ExtendedRelationship } from '@customTypes/constants';
import type { FVTReferralFamilyMemberCreateRequest, FVTReferralFamilyMemberDetails } from '@customTypes/referrals';
import { isMinorDOB } from '@utils/dates';
import { DOBValidator } from '@validators/subject';

export type FVTFamilyMemberFormValues = {
  email: string;
  first_name: string;
  last_name: string;
  dob: string;
  sex: string;
  relationship: string;
  guardian_email: string;
  guardian_first_name: string;
  guardian_last_name: string;
  guardian_relationship: string;
};

export type FVTFamilyMemberFormField = keyof FVTFamilyMemberFormValues;

export type FVTFamilyMemberDependentFormField = Extract<FVTFamilyMemberFormField, 'email' | 'relationship'>;
export type FVTFamilyMemberDependentFieldErrors = Record<FVTFamilyMemberDependentFormField, string | undefined>;

export const getInitialInviteValues = (): FVTFamilyMemberFormValues => ({
  email: '',
  first_name: '',
  last_name: '',
  dob: '',
  sex: '',
  relationship: '',
  guardian_email: '',
  guardian_first_name: '',
  guardian_last_name: '',
  guardian_relationship: '',
});

const emailFormatSchema = Yup.string().email('Enter a valid email address.');

const relationshipSchema = Yup.string<ExtendedRelationship>()
  .oneOf(EXTENDED_RELATIONSHIP_VALUES, 'Select a relationship.')
  .required('Select a relationship.');

// Cast: Yup `.when` widens fields to `string | undefined`.
export const inviteRelativeSchema = Yup.object({
  email: emailFormatSchema.when('dob', {
    is: (dob: string) => !isMinorDOB(dob ?? ''),
    then: schema => schema.required('Email is required.'),
  }),
  relationship: relationshipSchema,
  first_name: Yup.string().trim().required('First name is required.'),
  last_name: Yup.string().trim().required('Last name is required.'),
  dob: DOBValidator.required('Date of birth is required.'),
  sex: Yup.string().oneOf(BIOLOGICAL_SEX_VALUES, 'Select a sex.').required('Select a sex.'),
  guardian_email: emailFormatSchema
    .when('dob', {
      is: (dob: string) => isMinorDOB(dob ?? ''),
      then: schema => schema.required("Guardian's email is required for a minor."),
      otherwise: schema => schema.transform(() => '').notRequired(),
    })
    .test({
      message: "Guardian's email must differ from the relative's email.",
      test: (value, context) => {
        const memberEmail: string = context.parent.email ?? '';
        if (!value || !memberEmail) {
          return true;
        }
        return value.trim().toLowerCase() !== memberEmail.trim().toLowerCase();
      },
    }),
  guardian_first_name: Yup.string()
    .trim()
    .when('dob', {
      is: (dob: string) => isMinorDOB(dob ?? ''),
      then: schema => schema.required("Guardian's first name is required for a minor."),
      otherwise: schema => schema.transform(() => '').notRequired(),
    }),
  guardian_last_name: Yup.string()
    .trim()
    .when('dob', {
      is: (dob: string) => isMinorDOB(dob ?? ''),
      then: schema => schema.required("Guardian's last name is required for a minor."),
      otherwise: schema => schema.transform(() => '').notRequired(),
    }),
  guardian_relationship: Yup.string()
    .trim()
    .when('dob', {
      is: (dob: string) => isMinorDOB(dob ?? ''),
      then: schema => schema.required("Guardian's relationship is required for a minor."),
      otherwise: schema => schema.transform(() => '').notRequired(),
    }),
}).required() as Yup.ObjectSchema<FVTFamilyMemberFormValues>;

export const toFamilyMemberCreateRequest = (
  values: FVTFamilyMemberFormValues,
): FVTReferralFamilyMemberCreateRequest => {
  const minor = isMinorDOB(values.dob);
  const trimmedEmail = values.email.trim();

  const request: FVTReferralFamilyMemberCreateRequest = {
    first_name: values.first_name.trim(),
    last_name: values.last_name.trim(),
    dob: values.dob,
    sex: values.sex as FVTReferralFamilyMemberCreateRequest['sex'],
    relationship: values.relationship as ExtendedRelationship,
    email: minor ? trimmedEmail || null : trimmedEmail,
  };

  if (minor) {
    request.guardian = {
      email: values.guardian_email.trim(),
      first_name: values.guardian_first_name.trim(),
      last_name: values.guardian_last_name.trim(),
      relationship: values.guardian_relationship.trim(),
    };
  }

  return request;
};

export const getDependentEmailError = (
  newEmail: string,
  familyMembers: FVTReferralFamilyMemberDetails[],
): string | undefined => {
  const normalizedEmail = newEmail.trim().toLowerCase();

  if (!normalizedEmail || !emailFormatSchema.isValidSync(normalizedEmail)) {
    return undefined;
  }

  const hasExisting = familyMembers.some(
    familyMember => (familyMember.email ?? '').trim().toLowerCase() === normalizedEmail,
  );

  if (hasExisting) {
    return 'This email is already associated with another family member.';
  }

  return undefined;
};

export const getDependentFieldErrors = (
  formValues: FVTFamilyMemberFormValues,
  familyMembers: FVTReferralFamilyMemberDetails[],
): FVTFamilyMemberDependentFieldErrors => {
  const emailError = getDependentEmailError(formValues.email, familyMembers);

  return {
    email: emailError,
    relationship: undefined,
  };
};
